import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import RestFill from '../components/RestFill';
import VideoButton from '../components/VideoButton';
import { formatSecs, useRestTimer } from '../hooks/useRestTimer';
import {
  PRE_WORKOUT_DRILLS,
  PRE_WORKOUT_NOTE,
  isTimed,
  loadCompleted,
  saveCompleted,
} from '../preWorkout';
import {
  clearJson, drillActiveKey, drillTimerKey, readJson, writeJson,
} from '../persistentTime';

/**
 * The general warm-up, shown as the first "exercise" of the day.
 *
 * Entirely client-side: the drills are identical every session, carry no load
 * and have nothing worth comparing or recording, so nothing here is sent to the
 * API. Ticks and timers live in localStorage purely so they survive navigating
 * around, refreshing, and closing the browser mid-workout.
 *
 * Every drill has a Start button that times it. Cardio is the only one with a
 * target: it counts down to five minutes, turns green (you may stop any time
 * from there), then runs on to ten. The rest are plain stopwatches.
 */
export default function PreWorkoutPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [done, setDone] = useState<number[]>([]);
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    setDone(loadCompleted(sessionId));
    // Resume whichever drill was being timed before a refresh or navigation.
    setActive(readJson<number>(drillActiveKey(sessionId)));
  }, [sessionId]);

  const activeDrill = active == null ? null : PRE_WORKOUT_DRILLS[active];
  const timed = activeDrill != null && isTimed(activeDrill);

  // One timer at a time — the drills are done in sequence. Untimed drills pass
  // no window, which makes the hook a plain stopwatch.
  const timer = useRestTimer(
    timed ? activeDrill!.readyAtSecs : null,
    timed ? activeDrill!.fullDurationSecs : null,
    sessionId ? drillTimerKey(sessionId) : undefined,
  );

  function persist(next: number[]) {
    setDone(next);
    if (sessionId) saveCompleted(sessionId, next);
  }

  function start(i: number) {
    setActive(i);
    if (sessionId) writeJson(drillActiveKey(sessionId), i);
    timer.start();
  }

  function finish(i: number) {
    if (!done.includes(i)) persist([...done, i]);
    timer.stop();
    setActive(null);
    if (sessionId) clearJson(drillActiveKey(sessionId));
  }

  function undo(i: number) {
    persist(done.filter(d => d !== i));
  }

  return (
    <>
    <SiteHeader back={{ label: 'Workout', to: `/workout/${sessionId}` }} />
    <main className="page page--narrow">
      <p className="muted">Pre-workout</p>
      <h1>General warm-up</h1>

      <section className="notes-panel">
        <h3>Notes</h3>
        <p>{PRE_WORKOUT_NOTE}</p>
      </section>

      {/* Always rendered, so starting a drill never reflows the page. */}
      <section
        className={`rest-timer rest-timer--${active == null ? 'idle' : timed ? timer.phase : 'stopwatch'}`}
        aria-live="polite"
      >
        {active != null && timed && (
          <RestFill
            key={`${timer.startedAt}-${timer.phase}`}
            total={timer.segmentTotal}
            elapsed={timer.segmentElapsed}
            mode={timer.phase === 'ready' ? 'drain' : 'fill'}
          />
        )}
        <div className="rest-timer-row">
          <span className="rest-timer-time">
            {active == null
              ? '0:00'
              : timed
                ? (timer.phase === 'over' ? `+${formatSecs(timer.overBy)}` : formatSecs(timer.countdown))
                : formatSecs(timer.elapsed)}
          </span>
          <span className="rest-timer-label">
            {active == null && 'Start the next warm-up'}
            {active != null && !timed && 'Timing this drill'}
            {active != null && timed && timer.phase === 'resting' && 'Keep going — until you can stop'}
            {active != null && timed && timer.phase === 'ready' && 'You can stop any time now'}
            {active != null && timed && timer.phase === 'over' && 'Full ten minutes done'}
          </span>
          {active != null && (
            <button type="button" className="btn-primary btn-sm" onClick={() => finish(active)}>
              Done
            </button>
          )}
        </div>
      </section>

      <ul className="set-list" style={{ marginTop: 16 }}>
        {PRE_WORKOUT_DRILLS.map((drill, i) => {
          const isDone = done.includes(i);
          const isActive = active === i;
          return (
            <li
              key={drill.name}
              className={`set-row drill-row${isDone ? ' set-row--done' : ''}${isActive ? ' set-row--next' : ''}`}
            >
              <div className="drill-row-main">
                <span className="set-row-label set-row-label--warmup">W{i + 1}</span>

                {/* The weight stepper is replaced by the drill's name, and the
                    prescription is text — none of it is a number to step. */}
                <span className="drill-name">
                  {drill.name}
                  {drill.optional && <span className="badge">optional</span>}
                  <VideoButton exerciseName={drill.videoName ?? drill.name} />
                </span>

                <span className="drill-reps">{drill.prescription}</span>

                {isDone ? (
                  <button type="button" className="btn-quiet btn-sm" onClick={() => undo(i)}>
                    Undo
                  </button>
                ) : isActive ? (
                  <button type="button" className="btn-primary btn-sm" onClick={() => finish(i)}>
                    Done
                  </button>
                ) : (
                  <button type="button" className="btn-secondary btn-sm" onClick={() => start(i)}>
                    Start
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Same shape as the exercise pages: step back on the left, move
          forward on the right. Exercises are numbered from 1, so the first is
          reachable without loading the session here. */}
      <div className="page-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate(`/workout/${sessionId}`)}
        >
          ← Workout
        </button>
        <button
          type="button"
          className="btn-primary btn-lg"
          onClick={() => navigate(`/workout/${sessionId}/1`)}
        >
          Start first exercise →
        </button>
      </div>
    </main>
    </>
  );
}
